// client/src/components/TopUpFiatModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const DEBUG_HTTP = true;
const mask = (t = '') =>
  t && String(t).length > 16 ? `${t.slice(0, 10)}…${t.slice(-6)}` : t;

// ✅ Минималка — совпадает с бэком
const MIN_RUB = 3000;

const styles = {
  card: {
    padding: 20,
    background: '#0f172a',
    color: '#e5e7eb',
    borderRadius: 16,
    width: 420,
    boxShadow: '0 10px 30px rgba(0,0,0,.35)',
    border: '1px solid #1f2937',
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  h3: { fontSize: 20, margin: '0 0 4px', fontWeight: 700 },
  sub: { fontSize: 12, color: '#9ca3af' },

  // плашка «карты РФ»
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    background: 'rgba(34,197,94,0.12)',
    color: '#bbf7d0',
    border: '1px solid rgba(34,197,94,0.4)',
    marginBottom: 6,
  },

  // ряд иконок карт
  iconsRow: {
    display: 'flex',
    gap: 6,
  },
  iconCard: {
    width: 52,
    height: 34,
    borderRadius: 8,
    padding: 4,
    background: 'linear-gradient(145deg,#020617,#0b1220)',
    border: '1px solid rgba(148,163,184,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconSvg: { width: '100%', height: '100%' },

  input: {
    width: '100%',
    height: 44,
    fontSize: 16,
    color: '#111827',
    background: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: 10,
    padding: '0 12px',
  },
  btnPrimary: {
    width: '100%',
    height: 44,
    borderRadius: 10,
    border: 'none',
    background: '#22c55e',
    color: '#0b2915',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
  link: { color: '#93c5fd', textDecoration: 'underline', fontSize: 13 },
};

export default function TopUpFiatModal() {
  const { t: translate } = useTranslation();
  // только рубли
  const [amount, setAmount] = useState(MIN_RUB);
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [error, setError] = useState('');

  // API origin
  const API_ORIGIN_RAW = process.env.REACT_APP_API_URL || '';
  const API_ORIGIN = API_ORIGIN_RAW.replace(/\/+$/, ''); // без хвостового слэша

  // axios-инстанс + токен + лог
  const http = useMemo(() => axios.create(), []);

  useEffect(() => {
    const reqId = http.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        if (DEBUG_HTTP) {
          console.log('%c[HTTP →] request', 'color:#0b74de;font-weight:bold;', {
            method: config.method,
            url: config.baseURL
              ? `${config.baseURL}${config.url}`
              : config.url,
            headers: config.headers,
            data: config.data,
          });
        }
        return config;
      },
      (e) => {
        DEBUG_HTTP &&
          console.log(
            '%c[HTTP →] request ERROR',
            'color:#d14343;font-weight:bold;',
            e
          );
        return Promise.reject(e);
      }
    );

    const resId = http.interceptors.response.use(
      (res) => {
        DEBUG_HTTP &&
          console.log(
            '%c[HTTP ←] response',
            'color:#1a7f37;font-weight:bold;',
            {
              url: res?.config?.url,
              status: res?.status,
              data: res?.data,
            }
          );
        return res;
      },
      (e) => {
        if (DEBUG_HTTP) {
          if (e?.response) {
            console.log(
              '%c[HTTP ←] response ERROR',
              'color:#d14343;font-weight:bold;',
              {
                url: e?.response?.config?.url,
                status: e?.response?.status,
                data: e?.response?.data,
              }
            );
          } else {
            console.log(
              '%c[HTTP ←] network ERROR',
              'color:#d14343;font-weight:bold;',
              e?.message || e
            );
          }
        }
        return Promise.reject(e);
      }
    );

    return () => {
      http.interceptors.request.eject(reqId);
      http.interceptors.response.eject(resId);
    };
  }, [http]);

  useEffect(() => {
    if (!DEBUG_HTTP) return;
    const token = localStorage.getItem('token');
    console.log('[TopUpFiatModal] mounted', {
      API_ORIGIN,
      tokenMasked: mask(token),
    });
  }, [API_ORIGIN]);

  // подпись минималки (всегда RUB)
  const minLabel = useMemo(() => `${MIN_RUB} ₽`, []);

  const createPayment = async () => {
    try {
      setLoading(true);
      setError('');
      const raw = Number(amount);
      if (!Number.isFinite(raw)) throw new Error(translate('profile.fiatTopUp.enterNumber'));

      const rub = Math.round(raw);
      if (rub < MIN_RUB) {
        throw new Error(translate('profile.fiatTopUp.minimumError', { amount: MIN_RUB }));
      }

      const payload = {
        amount: rub,
        currency: 'RUB',
        order_id: 'order_' + Date.now(),
      };

      const url = `${API_ORIGIN}/api/skycryptoPayments/create-payment`;
      const { data } = await http.post(url, payload, { timeout: 15000 });

      if (data?.paymentLink) {
        const pending = {
          // SkyPay иногда возвращает id, иногда только ссылку — сохраним всё
          paymentId: data.paymentId || null,
          orderId: payload.order_id,
          amount: Number(data.amount ?? payload.amount) || null,
          currency: String(data.currency ?? 'RUB'),
          createdAt: Date.now(),
        };
        try {
          localStorage.setItem('skypay:pending', JSON.stringify(pending));
        } catch {}
        try {
          if (pending.paymentId) {
            localStorage.setItem(
              'skypay:lastPaymentId',
              String(pending.paymentId)
            );
          }
        } catch {}
        setPaymentLink(data.paymentLink);
        window.open(data.paymentLink, '_blank');
      } else {
        throw new Error(translate('profile.fiatTopUp.linkMissing'));
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e.message ||
        translate('profile.fiatTopUp.createError');
      setError(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.card}>
      {/* шапка с плашкой и иконками карт */}
      <div style={styles.headerRow}>
        <div>
          <div style={styles.badge}>🇷🇺 {translate('profile.fiatTopUp.badge')}</div>
          <h3 style={styles.h3}>{translate('profile.fiatTopUp.title')}</h3>
          <div style={styles.sub}>
            {translate('profile.fiatTopUp.subtitle')}
          </div>
        </div>

        <div style={styles.iconsRow}>
          {/* Условная MIR-карта */}
          <div style={styles.iconCard}>
            <svg viewBox="0 0 80 52" style={styles.iconSvg}>
              <rect x="2" y="4" width="76" height="44" rx="8" fill="#022c22" />
              <rect
                x="2"
                y="4"
                width="76"
                height="44"
                rx="8"
                stroke="#16a34a"
                fill="none"
              />
              <text
                x="50%"
                y="50%"
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize="16"
                fontWeight="700"
                fill="#bbf7d0"
              >
                MIR
              </text>
            </svg>
          </div>

          {/* Условная Visa-карта */}
          <div style={styles.iconCard}>
            <svg viewBox="0 0 80 52" style={styles.iconSvg}>
              <rect x="2" y="4" width="76" height="44" rx="8" fill="#0f172a" />
              <rect
                x="2"
                y="4"
                width="76"
                height="44"
                rx="8"
                stroke="#3b82f6"
                fill="none"
              />
              <text
                x="50%"
                y="50%"
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize="14"
                fontWeight="700"
                fill="#bfdbfe"
              >
                VISA
              </text>
            </svg>
          </div>

          {/* Условная MasterCard-карта */}
          <div style={styles.iconCard}>
            <svg viewBox="0 0 80 52" style={styles.iconSvg}>
              <rect x="2" y="4" width="76" height="44" rx="8" fill="#020617" />
              <rect
                x="2"
                y="4"
                width="76"
                height="44"
                rx="8"
                stroke="#f97316"
                fill="none"
              />
              <circle cx="34" cy="26" r="9" fill="#f97316" />
              <circle cx="46" cy="26" r="9" fill="#e11d48" />
            </svg>
          </div>
        </div>
      </div>

      {/* форма только в RUB */}
      <div style={styles.sub}>
        {translate('profile.fiatTopUp.minimumText', { amount: MIN_RUB })}
      </div>

      <div style={{ marginTop: 12 }}>
        <input
          type="number"
          min={MIN_RUB}
          step={1}
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value || 0))}
          style={styles.input}
        />
        <div style={{ height: 10 }} />
        <button
          onClick={createPayment}
          disabled={loading || Number(amount) <= 0}
          style={styles.btnPrimary}
        >
          {loading ? translate('profile.fiatTopUp.creating') : translate('profile.fiatTopUp.payRub')}
        </button>

        {error && (
          <div style={{ color: '#fecaca', marginTop: 8 }}>{error}</div>
        )}

        {paymentLink && (
          <div style={{ marginTop: 10 }}>
            <a
              href={paymentLink}
              style={styles.link}
              target="_blank"
              rel="noreferrer"
            >
              {translate('profile.fiatTopUp.openAgain')}
            </a>
          </div>
        )}

        <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
          {translate('profile.fiatTopUp.minimumLabel')}: {minLabel}
        </div>
      </div>
    </div>
  );
}
